import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { FiFileText } from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import {
  getScopeUpdates,
  getProjectScopesForUpdate,
} from "../../../../services/scopeUpdateService";
import "./ProjectUpdates.css";

const DEFAULT_IMAGE =
  "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";
const PER_PAGE = 20;

export default function ProjectUpdates({ projectId, projectDetails }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState(null);
  const [scopesData, setScopesData] = useState(null);
  const [expandedDates, setExpandedDates] = useState(() => new Set());

  const loadList = async (nextPage = page) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getScopeUpdates(projectId, {
        page: nextPage,
        perPage: PER_PAGE,
      });
      if (res?.success) setListData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadScopes = async () => {
    if (!projectId) return;
    try {
      const res = await getProjectScopesForUpdate(projectId);
      if (res?.success) setScopesData(res);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadList(1);
    setPage(1);
    loadScopes();
    setExpandedDates(new Set());
  }, [projectId]);

  useEffect(() => {
    loadList(page);
    setExpandedDates(new Set());
  }, [page]);

  const allContractedScopes = useMemo(() => {
    if (!scopesData?.scopes || !Array.isArray(scopesData.scopes)) return [];
    const allowed = ["ongoing", "in_warranty"];
    return scopesData.scopes.filter((scope) =>
      scope.bids?.some((b) => allowed.includes(b.status)),
    );
  }, [scopesData]);

  const totalScopesFromApi = listData?.total_scopes ?? 0;
  const totalScopes = allContractedScopes.length || totalScopesFromApi || 0;

  const totalCount = listData?.total_count ?? 0;
  const totalPages = listData?.total_pages ?? 1;
  const prevEnable = listData?.prev_enable ?? 0;
  const nextEnable = listData?.next_enable ?? 0;

  const today = moment().format("YYYY-MM-DD");
  const todaysGroup = listData?.data?.find((g) => g.update_date === today);
  const todaysCoveredScopes = todaysGroup?.covered_scopes ?? 0;

  const toggleAccordion = (date) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const buildDetailUrl = (updateId, updateDate, scopeId) => {
    const params = new URLSearchParams();
    if (updateDate) params.set("date", updateDate);
    if (scopeId != null) params.set("scopeId", String(scopeId));
    const qs = params.toString();
    return `/project/details/${projectId}/project-updates/${updateId}${
      qs ? `?${qs}` : ""
    }`;
  };

  const handleScopeClick = (scopeUpdate) => {
    navigate(
      buildDetailUrl(
        String(scopeUpdate.update_id),
        scopeUpdate.update_date,
        scopeUpdate.scope_id,
      ),
    );
  };

  const handleAddDailyUpdate = () => {
    navigate(buildDetailUrl("new", today, null));
  };

  const handleAddScopeUpdate = (dateGroup) => {
    const usedIds = new Set(
      (dateGroup.scope_updates || []).map((s) => s.scope_id),
    );
    const remaining = allContractedScopes.filter(
      (s) => !usedIds.has(s.scope_id),
    );
    if (remaining.length === 0) return;
    navigate(
      buildDetailUrl("new", dateGroup.update_date, remaining[0].scope_id),
    );
  };

  const isEmptyList =
    listData && (!listData.data || listData.data.length === 0);

  return (
    <div className="pu-wrap">
      {isEmptyList && (
        <div className="pu-empty-row">
          <span className="pu-empty-text">No Progress Updates found</span>
          <button
            type="button"
            className="pu-add-daily-btn"
            onClick={handleAddDailyUpdate}
          >
            Add Daily Update
          </button>
        </div>
      )}

      <div className="pu-accordion-list">
        {listData?.data?.map((dateGroup) => {
          const isExpanded = expandedDates.has(dateGroup.update_date);
          const totalUpdates = dateGroup.total_updates ?? 0;
          const coveredScopes = dateGroup.covered_scopes ?? 0;
          const coveredActivities = dateGroup.covered_activities ?? 0;
          const isToday = dateGroup.update_date === today;
          const canAddScope =
            isToday && totalScopes > 0 && coveredScopes < totalScopes;

          return (
            <div key={dateGroup.update_date} className="pu-accordion">
              <div
                role="button"
                tabIndex={0}
                className="pu-accordion-header"
                onClick={() => toggleAccordion(dateGroup.update_date)}
                onKeyDown={(e) =>
                  e.key === "Enter" && toggleAccordion(dateGroup.update_date)
                }
              >
                <span className="pu-icon-file">
                  <FiFileText style={{ width: 20, height: 20 }} />
                </span>
                <div className="pu-header-text">
                  <div className="pu-header-title">
                    Update -{" "}
                    {moment(dateGroup.update_date).format("MMM DD, YYYY")}
                  </div>
                  <div className="pu-header-meta">
                    <span>
                      {totalUpdates} update{totalUpdates !== 1 ? "s" : ""}
                    </span>
                    <span>•</span>
                    <span>
                      {coveredScopes}
                      {totalScopes ? `/${totalScopes}` : ""} scope
                      {(totalScopes || coveredScopes) !== 1 ? "s" : ""}
                    </span>
                    <span>•</span>
                    <span>
                      {coveredActivities} activit
                      {coveredActivities !== 1 ? "ies" : "y"}
                    </span>
                  </div>
                </div>
                {canAddScope && (
                  <button
                    type="button"
                    className="pu-add-scope-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddScopeUpdate(dateGroup);
                    }}
                  >
                    Add Scope Update
                  </button>
                )}
                <span className="pu-chevron">
                  {isExpanded ? (
                    <IoChevronUp style={{ width: 20, height: 20 }} />
                  ) : (
                    <IoChevronDown style={{ width: 20, height: 20 }} />
                  )}
                </span>
              </div>
              {isExpanded && (
                <div className="pu-scope-list">
                  {dateGroup.scope_updates?.map((scope) => (
                    <div
                      role="button"
                      tabIndex={0}
                      key={scope.update_id}
                      className="pu-scope-row"
                      onClick={() => handleScopeClick(scope)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleScopeClick(scope)
                      }
                    >
                      <div className="pu-scope-thumb">
                        <img src={scope?.image?.path || DEFAULT_IMAGE} alt="" />
                      </div>
                      <div className="pu-scope-text">
                        <div className="pu-scope-name">{scope.update_name}</div>
                        <div className="pu-scope-sub">{scope.scope_name}</div>
                        <div className="pu-scope-sub">
                          {scope.update_date
                            ? moment(scope.update_date).format("MMM DD, YYYY")
                            : "—"}
                        </div>
                      </div>
                      <div className="pu-scope-images">
                        <CiImageOn style={{ width: 20, height: 20 }} />
                        <span>
                          {scope.total_images ?? 0} image
                          {(scope.total_images ?? 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isEmptyList && (!todaysGroup || todaysCoveredScopes === 0) && (
        <button
          type="button"
          className="pu-add-daily-btn"
          onClick={handleAddDailyUpdate}
        >
          Add Daily Update
        </button>
      )}

      {listData?.data?.length > 0 && totalPages > 1 && (
        <div className="pu-pagination">
          <span className="pu-pagination-text">
            Page {page} of {totalPages} ({totalCount} total)
          </span>
          <div className="pu-pagination-buttons">
            <button
              type="button"
              className="pu-pagination-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!prevEnable}
            >
              Previous
            </button>
            <button
              type="button"
              className="pu-pagination-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={!nextEnable}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {loading && listData == null && (
        <div style={{ textAlign: "center", padding: 40, color: "#949daa" }}>
          Loading...
        </div>
      )}
    </div>
  );
}
